import { useSuspenseQuery } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { useState } from 'react';
import { z } from 'zod';
import { latestDownloadsQueryOptions } from '#api/downloads.queries.ts';
import type { OSGroup } from '#dn-types/downloads.ts';
import type { HostNoun } from '#dn-types/host.ts';
import { useBrowserStorage } from '#hooks/useBrowserStorage.ts';
import { ArchitecturePicker } from '#screens/hosts/enroll/ArchitecturePicker.tsx';
import { CollapsedInstructions } from '#screens/hosts/enroll/docs/token-instructions/CollapsedInstructions.tsx';
import { SPR } from '#util/validation/zod-utils.ts';
import { DockerComposeInstructions } from '../docs/DockerComposeInstructions.tsx';
import { DockerInstructions } from '../docs/DockerInstructions.tsx';
import { InstallMethodPicker } from '../docs/InstallMethodPicker.tsx';
import { LinuxArchSelect } from '../docs/LinuxArchSelect.tsx';
import { LinuxInstructions } from '../docs/LinuxInstructions.tsx';
import { MacOSInstructions } from '../docs/MacOSInstructions.tsx';
import { MobileInstructions } from '../docs/MobileInstructions.tsx';
import type { LinuxInstallMethod } from '../docs/package-install.ts';
import { WindowsInstructions } from '../docs/WindowsInstructions.tsx';
import type { EnrollmentState } from '../types.ts';

type Props = {
  hostNoun: HostNoun;
  enrollmentState: EnrollmentState;
};

type ArchOption = Parameters<React.ComponentProps<typeof ArchitecturePicker>['changeAction']>[1];

export function ArchitectureTabs({ hostNoun, enrollmentState }: Props) {
  const [osGroup, setOsGroup] = useState<OSGroup>();
  const [arch, setArch] = useState<ArchOption | ''>();
  // Prefer the package repositories on Linux; 'binary' keeps the classic curl-and-chmod path.
  const [installMethod, setInstallMethod] = useState<LinuxInstallMethod>('apt');
  // Linux picks its architecture inside the binary-download step rather than as a top-level step.
  const [linuxArch, setLinuxArch] = useState<string>('linux-amd64');
  const [collapse, persistCollapsed] = useBrowserStorage<{ shouldCollapse: boolean }>('collapse-docs', 'local');
  const { data: parsedCollapse } = SPR(z.object({ shouldCollapse: z.boolean() }).safeParse(collapse));

  const [isCollapsed, setCollapsed] = useState<boolean>(parsedCollapse?.shouldCollapse ?? false);

  const { data: downloads } = useSuspenseQuery(latestDownloadsQueryOptions);

  function handleCollapseChange(shouldCollapse: boolean) {
    // Using an object here because bare boolean strings turn into booleans
    persistCollapsed({ shouldCollapse });
    setCollapsed(shouldCollapse);
  }

  const showMobile = hostNoun === 'host';

  let instructions: React.ReactNode | null = null;

  if (osGroup === 'Linux') {
    // No top-level arch gate: the method picker renders immediately, and only the
    // binary-download path cares about the architecture. The API lists every Linux arch
    // regardless of whether it serves a build, so only offer ones with a download.
    const archOptions = downloads.Linux.filter((a) => a.download !== undefined);
    const downloadCommand = archOptions.find((x) => x.key === linuxArch)?.download;

    // The method picker (and, for binaries, the arch select) stays visible while condensed,
    // so non-Debian users aren't stranded on the default APT commands.
    instructions = (
      <>
        <h2 className="h3">2. Select install method</h2>
        <InstallMethodPicker value={installMethod} onChange={setInstallMethod} />
        {isCollapsed ?
          <CollapsedInstructions
            hostNoun={hostNoun}
            enrollmentState={enrollmentState}
            platform={osGroup}
            stepNumber={3}
            downloadCommand={downloadCommand}
            installMethod={installMethod}
            onExpand={() => handleCollapseChange(false)}
          >
            {installMethod === 'binary' ?
              <LinuxArchSelect
                className="mb-24"
                archOptions={archOptions}
                arch={linuxArch}
                onArchChange={setLinuxArch}
              />
            : null}
          </CollapsedInstructions>
        : <LinuxInstructions
            hostNoun={hostNoun}
            enrollmentState={enrollmentState}
            downloadCommand={downloadCommand}
            archOptions={archOptions}
            arch={linuxArch}
            onArchChange={setLinuxArch}
            installMethod={installMethod}
            onCollapse={() => handleCollapseChange(true)}
          />
        }
      </>
    );
  } else if (osGroup === 'Mobile') {
    // The enroll flow is identical on iOS and Android, so there's no version step: link both stores.
    instructions = (
      <MobileInstructions
        enrollmentState={enrollmentState}
        iosURL={downloads.Mobile.find((a) => a.key === 'ios')?.url}
        androidURL={downloads.Mobile.find((a) => a.key === 'android')?.url}
      />
    );
  } else if (osGroup && arch) {
    const info = downloads[osGroup].find((x) => x.key === arch);
    const downloadCommand = info && info.type === 'download' ? info.download : undefined;

    if (osGroup === 'macOS') {
      instructions = (
        <MacOSInstructions
          hostNoun={hostNoun}
          enrollmentState={enrollmentState}
          downloadURL={assert(info?.url, `Expected to have a download url for macOS Desktop: ${JSON.stringify(info)}`)}
        />
      );
    } else if (osGroup === 'Windows') {
      instructions = (
        <WindowsInstructions
          hostNoun={hostNoun}
          enrollmentState={enrollmentState}
          downloadURL={assert(
            info?.url,
            `Expected to have a download url for Windows Desktop: ${JSON.stringify(info)}`
          )}
        />
      );
    } else {
      const archInfo = downloads[osGroup].find((x) => x.key === arch);
      const url = archInfo && archInfo.type === 'url' ? archInfo.url : undefined;
      if (arch === 'docker') {
        instructions = <DockerInstructions enrollmentState={enrollmentState} hostNoun={hostNoun} url={url} />;
      } else if (arch === 'docker-compose') {
        instructions = <DockerComposeInstructions enrollmentState={enrollmentState} hostNoun={hostNoun} url={url} />;
      } else if (arch === 'freebsd-amd64' || arch === 'freebsd-arm64') {
        instructions =
          isCollapsed ?
            <CollapsedInstructions
              hostNoun={hostNoun}
              enrollmentState={enrollmentState}
              platform="FreeBSD"
              stepNumber={3}
              downloadCommand={downloadCommand}
              onExpand={() => handleCollapseChange(false)}
            />
          : <LinuxInstructions
              hostNoun={hostNoun}
              enrollmentState={enrollmentState}
              downloadCommand={downloadCommand}
              onCollapse={() => handleCollapseChange(true)}
            />;
      }
    }
  } else {
    instructions = null;
  }

  return (
    <div>
      <ArchitecturePicker
        downloads={downloads}
        includeMobile={showMobile}
        enrollmentToken={enrollmentState.token}
        changeAction={(os, architecture) => {
          setOsGroup(os);
          setArch(architecture);
        }}
      />
      {instructions}
    </div>
  );
}
